/**
 * Helpers for recursively instantiating relationship data in generated load() methods.
 *
 * Used by codegen to replace per-field inline blocks with single function calls.
 */

import { RelationshipManager } from './relationship-manager';

type ModelRegistryType = Record<string, { load?: (data: any) => any }>;

/**
 * Load a to-one relationship field from raw data into a class instance.
 *
 * Checks that the value is a plain object (not a RelationshipManager),
 * resolves the correct model class via `_label` (for union types) or the
 * provided default, calls `ModelClass.load()`, and writes the result to
 * the private `_fieldName` property.  The public property is deleted so
 * Object.assign() in the constructor won't shadow the getter.
 */
export function loadOneRelationship(
  processed: any,
  fieldName: string,
  defaultModel: string,
  registry: ModelRegistryType,
): void {
  const value = processed[fieldName];
  if (!value) return;

  if (typeof value === 'object' && !(value instanceof RelationshipManager)) {
    const label = value._label;
    const ModelClass = label ? registry[label] : registry[defaultModel];
    if (ModelClass?.load) {
      processed[`_${fieldName}`] = ModelClass.load(value);
    }
  }
  delete processed[fieldName];
}

/**
 * Load a to-many relationship field from raw data into class instances.
 *
 * Maps each array element through `ModelClass.load()`, using `_label` per
 * item to support polymorphic collections.  Writes to `_fieldName` and
 * deletes the public property.
 */
export function loadManyRelationship(
  processed: any,
  fieldName: string,
  defaultModel: string,
  registry: ModelRegistryType,
): void {
  const value = processed[fieldName];
  if (!value) return;

  const DefaultClass = registry[defaultModel];
  if (DefaultClass?.load && Array.isArray(value)) {
    processed[`_${fieldName}`] = (value as any[]).map(item => {
      const itemClass = item._label ? registry[item._label] : DefaultClass;
      return itemClass?.load ? itemClass.load(item) : item;
    });
  }
  delete processed[fieldName];
}
